import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { makeMeshNodes } from "@/components/site/mesh-geometry";
import { MeshSvg } from "@/components/site/MeshSvg";

const NetworkMesh = lazy(() => import("@/components/site/NetworkMesh").then((m) => ({ default: m.NetworkMesh })));

/**
 * Hero background mesh. Server-renders a static poster (identical seeded nodes);
 * the animated version loads only after window load + idle, on screens >= 768px,
 * without reduced-motion or Save-Data, and runs only while visible.
 */
export function HeroMesh({ className, variant = "dark" }: { className?: string; variant?: "light" | "dark" }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [animate, setAnimate] = useState(false);
  const [count, setCount] = useState(42);
  const [visible, setVisible] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);
  const staticNodes = useMemo(() => makeMeshNodes(42), []);

  useEffect(() => {
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      conn?.saveData ||
      window.innerWidth < 768
    ) {
      return;
    }
    let cancelled = false;
    let idleId: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const start = () => {
      if (cancelled) return;
      setCount(window.innerWidth < 1200 ? 30 : 42);
      setAnimate(true);
    };
    const schedule = () => {
      const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
      if (w.requestIdleCallback) idleId = w.requestIdleCallback(start, { timeout: 4000 });
      else timer = setTimeout(start, 2000);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      if (timer) clearTimeout(timer);
      const w = window as Window & { cancelIdleCallback?: (id: number) => void };
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
    };
  }, []);

  useEffect(() => {
    if (!animate || !wrapRef.current) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting));
    io.observe(wrapRef.current);
    const onVis = () => setTabVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [animate]);

  const poster = (
    <MeshSvg nodes={staticNodes} linkDistance={16} variant={variant} className="h-full w-full" gradientId="mesh-stroke-static" />
  );

  return (
    <div ref={wrapRef} className={className} aria-hidden="true">
      {animate ? (
        <Suspense fallback={poster}>
          <NetworkMesh count={count} variant={variant} className="h-full w-full" paused={!visible || !tabVisible} />
        </Suspense>
      ) : (
        poster
      )}
    </div>
  );
}
